import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Loader2, Printer, ArrowLeft } from "lucide-react";
import { format } from "date-fns";

export default function EventTicket() {
  const { businessSlug, eventSlug, registrationId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const run = async () => {
      if (!registrationId || !businessSlug || !eventSlug) {
        setError("Missing registration information");
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      
      try {
        // Fetch registration with event and business details
        const { data: regData, error: regError } = await supabase
          .from("event_registrations")
          .select(`
            id,
            ticket_code,
            name,
            email,
            phone,
            quantity,
            status,
            unit_price_amount,
            unit_currency,
            ticket_type_name,
            created_at,
            event:events(
              id,
              title,
              starts_at,
              ends_at,
              venue_name,
              venue_address,
              cover_image_url,
              business:businesses(
                id,
                name,
                phone,
                logo_url,
                primary_color,
                location_id,
                booking_deposit_payment_instructions
              )
            )
          `)
          .eq("id", registrationId)
          .limit(1)
          .maybeSingle();
        
        if (regError) throw regError;
        if (!regData) {
          setError("Registration not found");
          setLoading(false);
          return;
        }

        // Fetch business location address
        let businessAddress = null;
        const business = (regData.event as any)?.business;
        if (business?.id) {
          if (business.location_id) {
            const { data: location } = await supabase
              .from("locations")
              .select("address")
              .eq("id", business.location_id)
              .maybeSingle();
            businessAddress = location?.address || null;
          } else {
            // Fallback to default location
            const { data: location } = await supabase
              .from("locations")
              .select("address")
              .eq("business_id", business.id)
              .eq("is_default", true)
              .maybeSingle();
            businessAddress = location?.address || null;
          }
        }
        
        // Fetch payment info if paid via Paystack
        let paymentInfo = null;
        if (regData.status === "registered" && regData.id) {
          const { data: payment } = await supabase
            .from("platform_payments")
            .select("provider_reference, paid_at, status, gross_amount, currency")
            .eq("context", "event_registration")
            .eq("context_id", regData.id)
            .eq("status", "success")
            .order("paid_at", { ascending: false })
            .limit(1)
            .maybeSingle();
          paymentInfo = payment;
        }
        
        setData({ ...regData, paymentInfo, businessAddress });
      } catch (e: any) {
        setError(e?.message || "Failed to load ticket");
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [registrationId, businessSlug, eventSlug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="max-w-md mx-auto">
          <p className="text-sm text-destructive mb-4">{error || "Ticket not found"}</p>
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  const event = data.event as any;
  const business = event?.business;
  const printedAt = format(new Date(), "MMM d, yyyy h:mm a");
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(data.ticket_code)}`;
  const businessAddress = data.businessAddress || null;
  const startDate = event?.starts_at ? format(new Date(event.starts_at), "MMM d, yyyy") : "—";
  const startTime = event?.starts_at ? format(new Date(event.starts_at), "h:mm a") : "—";
  const endDate = event?.ends_at ? format(new Date(event.ends_at), "MMM d, yyyy") : null;
  const endTime = event?.ends_at ? format(new Date(event.ends_at), "h:mm a") : null;
  const totalAmount = data.unit_price_amount != null ? Number(data.unit_price_amount) * Number(data.quantity || 1) : null;
  const currency = data.unit_currency || "GHS";
  const isPendingPayment = data.status === "pending" && totalAmount != null && totalAmount > 0;
  const paymentInstructions = String(business?.booking_deposit_payment_instructions || "").trim();

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="mx-auto max-w-md">
        <div className="flex items-center justify-between gap-3 mb-4 print:hidden">
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <Button onClick={() => window.print()}>
            <Printer className="mr-2 h-4 w-4" />
            Print
          </Button>
        </div>

        <div className="rounded-xl border-2 border-border bg-card p-6 print:border-2 print:shadow-none print:rounded-none print:p-4">
          {/* Business Header */}
          <div className="text-center mb-4">
            {business?.logo_url ? (
              <img 
                src={business.logo_url} 
                alt={business.name || "Business"} 
                className="h-12 mx-auto mb-2 object-contain"
              />
            ) : null}
            <p className="text-xl font-bold text-foreground">{business?.name || "Buukd"}</p>
            {businessAddress ? (
              <p className="text-xs text-muted-foreground mt-1">{businessAddress}</p>
            ) : null}
            {business?.phone ? (
              <p className="text-xs text-muted-foreground mt-1">{business.phone}</p>
            ) : null}
          </div>

          <div className="border-t-2 border-border pt-4 mb-4">
            <p className="text-xs text-muted-foreground text-center uppercase tracking-wider font-semibold">
              {isPendingPayment ? "Pending Event Registration" : "Event Ticket"}
            </p>
            <p className="mt-2 text-center font-mono text-3xl tracking-widest font-bold">{data.ticket_code}</p>
            {isPendingPayment ? (
              <p className="mt-2 rounded-full bg-amber-100 px-3 py-1 text-center text-xs font-semibold text-amber-800">
                Payment pending
              </p>
            ) : null}
          </div>

          {/* Event Details */}
          <div className="mb-4 space-y-2">
            <h2 className="text-lg font-bold text-foreground text-center">{event?.title || "Event"}</h2>
            {data.ticket_type_name && (
              <p className="text-sm text-muted-foreground text-center">{data.ticket_type_name}</p>
            )}
          </div>

          {/* QR Code */}
          <div className="flex justify-center my-4">
            <img 
              src={qrCodeUrl} 
              alt="Ticket QR Code" 
              className="h-40 w-40 border-2 border-border rounded-lg p-2 bg-white print:h-36 print:w-36"
              onError={(e) => {
                console.error("QR code failed to load:", qrCodeUrl);
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
          </div>

          {/* Attendee & Event Details */}
          <div className="mt-4 space-y-2 text-sm">
            <div className="flex items-center justify-between py-1">
              <span className="text-muted-foreground">Name</span>
              <span className="font-semibold text-foreground text-right">{data.name || "—"}</span>
            </div>
            {data.email && (
              <div className="flex items-center justify-between py-1">
                <span className="text-muted-foreground">Email</span>
                <span className="font-medium text-foreground text-right text-xs">{data.email}</span>
              </div>
            )}
            {data.phone && (
              <div className="flex items-center justify-between py-1">
                <span className="text-muted-foreground">Phone</span>
                <span className="font-medium text-foreground text-right">{data.phone}</span>
              </div>
            )}
            <div className="flex items-center justify-between py-1">
              <span className="text-muted-foreground">Quantity</span>
              <span className="font-medium text-foreground">{Number(data.quantity || 1)} {Number(data.quantity || 1) > 1 ? "tickets" : "ticket"}</span>
            </div>
            <div className="border-t border-border pt-2 mt-2">
              <div className="flex items-center justify-between py-1">
                <span className="text-muted-foreground">Date</span>
                <span className="font-medium text-foreground">{startDate}</span>
              </div>
              <div className="flex items-center justify-between py-1">
                <span className="text-muted-foreground">Time</span>
                <span className="font-medium text-foreground">
                  {startTime}{endTime ? ` – ${endTime}` : ""}
                </span>
              </div>
              {endDate && endDate !== startDate && (
                <div className="flex items-center justify-between py-1">
                  <span className="text-muted-foreground">End Date</span>
                  <span className="font-medium text-foreground">{endDate}</span>
                </div>
              )}
              {event?.venue_name && (
                <div className="flex items-start justify-between py-1">
                  <span className="text-muted-foreground">Venue</span>
                  <span className="font-medium text-foreground text-right">{event.venue_name}</span>
                </div>
              )}
              {event?.venue_address && (
                <div className="flex items-start justify-between py-1">
                  <span className="text-muted-foreground">Address</span>
                  <span className="font-medium text-foreground text-right text-xs">{event.venue_address}</span>
                </div>
              )}
            </div>
          </div>

          {/* Payment Section */}
          {totalAmount != null && totalAmount > 0 && (
            <div className="mt-4 pt-4 border-t border-border">
              <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase">Payment Details</p>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Total Amount</span>
                  <span className="font-bold text-foreground text-lg">
                    {currency} {totalAmount.toFixed(2)}
                  </span>
                </div>
                {data.paymentInfo?.provider_reference && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Payment Reference</span>
                    <span className="font-mono text-xs text-foreground">{data.paymentInfo.provider_reference}</span>
                  </div>
                )}
                {data.paymentInfo?.paid_at && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Paid At</span>
                    <span className="font-medium text-foreground text-xs">
                      {format(new Date(data.paymentInfo.paid_at), "MMM d, yyyy h:mm a")}
                    </span>
                  </div>
                )}
                {isPendingPayment && paymentInstructions ? (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-950">
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide">Payment Instructions</p>
                    <p className="whitespace-pre-wrap text-sm leading-relaxed">{paymentInstructions}</p>
                  </div>
                ) : null}
              </div>
            </div>
          )}

          {/* Terms & Footer */}
          <div className="mt-6 pt-4 border-t border-border">
            <p className="text-xs text-muted-foreground text-left leading-relaxed">
              {isPendingPayment
                ? "Your registration is pending payment confirmation. Present this code only after the business confirms payment."
                : "This ticket is valid for the event shown above. Please present this ticket or QR code at check-in."}
            </p>
            <p className="text-xs text-muted-foreground text-left mt-2">
              Printed {printedAt}
            </p>
            <p className="text-xs text-muted-foreground text-left mt-1">
              Ticket #: {data.ticket_code}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
